import React, { useState } from 'react';
import { MapPin, Building2, X } from 'lucide-react';
import { REGIONS } from '../mock/locations';
import { useLang } from '../contexts/LanguageContext';

const accentMap = {
  green: { dot: 'bg-gray-500', ring: 'bg-gray-500/30', text: 'text-green-400', badge: 'bg-gray-900/40 text-green-300 border-gray-700/50' },
  orange: { dot: 'bg-orange-500', ring: 'bg-orange-500/30', text: 'text-orange-400', badge: 'bg-orange-900/40 text-orange-300 border-orange-700/50' }
};

const WorldMap = ({ compact = false }) => {
  const { t } = useLang();
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);

  return (
    <section className="relative py-20 bg-gradient-to-b from-[#0e1116] via-[#12151a] to-[#0e1116] text-white overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:50px_50px]"></div>
      <div className="relative max-w-[1400px] mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-green-500 font-bold text-[12px] uppercase tracking-[0.25em] mb-3">{t('globalReach')}</div>
          <h2 className="text-4xl md:text-5xl font-black">Engineering Across <span className="text-orange-500">Continents</span></h2>
          <p className="text-gray-400 mt-4">{t('servingWorldwide')} — from Americas to Asia-Pacific. Click any region or pin to explore the cities & sub-regions we serve.</p>
        </div>

        {/* Region legend */}
        <div className="flex flex-wrap justify-center gap-3 mb-6">
          {REGIONS.map(r => {
            const a = accentMap[r.accent];
            return (
              <button key={r.id} onClick={() => setSelected(r.id === selected ? null : r.id)} className={`group inline-flex items-center gap-2 px-4 py-2 rounded-full border ${selected === r.id ? a.badge : 'border-gray-900/40 text-gray-300 hover:border-green-500'} transition-colors text-xs font-bold uppercase tracking-wider`}>
                <span className={`w-2.5 h-2.5 rounded-full ${a.dot}`}></span>
                {r.name}
                <span className="text-[10px] opacity-60">({r.countries.length})</span>
              </button>
            );
          })}
        </div>

        {/* Map */}
        <div className="relative aspect-[2/1] bg-[#06090a] border border-gray-900/50 rounded-lg overflow-hidden">
          {/* Stylised continent silhouettes (simplified SVG paths). Light dotted overlay for tech look. */}
          <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            {/* dot grid pattern */}
            <defs>
              <pattern id="dots" x="0" y="0" width="1.2" height="1.2" patternUnits="userSpaceOnUse">
                <circle cx="0.2" cy="0.2" r="0.18" fill="#2a2f38" />
              </pattern>
            </defs>
            <rect width="100" height="50" fill="url(#dots)" />

            {/* Continent shapes (very simplified) */}
            <g fill="#1a1f2e" stroke="#64748b" strokeWidth="0.15" opacity="0.85">
              {/* North America */}
              <path d="M10 14 L24 12 L26 18 L24 26 L20 32 L14 34 L10 28 L8 22 Z" />
              {/* Central America */}
              <path d="M16 32 L20 33 L22 38 L20 42 L17 41 L15 37 Z" />
              {/* South America */}
              <path d="M22 42 L28 42 L30 48 L29 58 L26 65 L23 64 L21 56 L20 48 Z" />
              {/* Europe */}
              <path d="M44 18 L54 16 L56 24 L55 30 L48 32 L44 28 Z" />
              {/* Africa */}
              <path d="M48 32 L56 32 L58 40 L56 48 L52 50 L48 44 L46 38 Z" />
              {/* Middle East + west asia */}
              <path d="M55 28 L65 28 L66 36 L62 40 L56 38 Z" />
              {/* Asia */}
              <path d="M55 14 L82 12 L86 20 L82 28 L72 30 L64 26 L58 22 Z" />
              {/* India */}
              <path d="M66 28 L72 28 L74 36 L70 40 L66 36 Z" />
              {/* SE Asia */}
              <path d="M74 36 L82 36 L84 44 L80 47 L75 44 Z" />
              {/* Australia */}
              <path d="M80 60 L92 58 L94 66 L88 70 L82 68 Z" />
              {/* NZ */}
              <path d="M92 70 L95 71 L94 75 L91 74 Z" />
            </g>
          </svg>

          {/* Connection lines between hubs */}
          <svg viewBox="0 0 100 50" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg">
            <g stroke="#f97316" strokeWidth="0.08" fill="none" strokeDasharray="0.6 0.4" opacity="0.5">
              <path d="M18 19 Q35 12 51 15" />
              <path d="M51 15 Q60 25 70 22.5" />
              <path d="M70 22.5 Q78 25 85 36" />
              <path d="M18 19 Q42 8 78 19" />
              <path d="M51 15 Q62 22 63 22.5" />
            </g>
          </svg>

          {/* Pins */}
          {REGIONS.map(r => {
            const a = accentMap[r.accent];
            const dim = selected && selected !== r.id;
            return r.countries.map(c => (
              <button
                key={r.id + c.code}
                onClick={() => setHovered({ ...c, region: r.name, accent: r.accent })}
                onMouseEnter={() => setHovered({ ...c, region: r.name, accent: r.accent })}
                onMouseLeave={() => setHovered(null)}
                className={`absolute -translate-x-1/2 -translate-y-1/2 group ${dim ? 'opacity-25' : ''} transition-opacity`}
                style={{ left: `${c.x}%`, top: `${c.y}%` }}
                aria-label={c.name}
              >
                <span className={`absolute inset-0 ${a.ring} rounded-full ${c.hub ? 'w-6 h-6 -m-3' : 'w-4 h-4 -m-2'} animate-ping`}></span>
                <span className={`relative block ${a.dot} rounded-full ${c.hub ? 'w-3 h-3 ring-2 ring-white/40' : 'w-2 h-2'} shadow-lg`}></span>
                {c.hub && (
                  <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 text-[9px] font-bold tracking-wider whitespace-nowrap text-white">{c.code}</span>
                )}
              </button>
            ));
          })}

          {/* Hover card */}
          {hovered && (
            <div className="absolute bottom-3 left-3 right-3 md:left-auto md:right-3 md:bottom-3 md:max-w-xs bg-[#0e1116]/95 border border-gray-700/60 rounded-lg p-4 backdrop-blur-md shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <MapPin className={`w-4 h-4 ${accentMap[hovered.accent].text}`} />
                    <h4 className="font-bold text-base">{hovered.name}</h4>
                    {hovered.hub && <span className="text-[9px] bg-orange-500 text-white font-bold px-1.5 py-0.5 rounded uppercase">HUB</span>}
                  </div>
                  <div className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">{hovered.region}</div>
                </div>
                <button onClick={() => setHovered(null)} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {hovered.cities.slice(0, 8).map(city => (
                  <span key={city} className="text-[10px] bg-gray-900/40 border border-gray-700/40 text-green-200 px-2 py-0.5 rounded">{city}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sub-region grid */}
        {!compact && (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {REGIONS.map(r => {
              const a = accentMap[r.accent];
              return (
                <div key={r.id} className={`bg-[#1a1f2e] border border-gray-900/40 rounded-lg p-5 hover:border-green-500 transition-colors`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className={`w-4 h-4 ${a.text}`} />
                    <h4 className="font-bold uppercase tracking-wider text-sm">{r.name}</h4>
                  </div>
                  <ul className="space-y-1.5">
                    {r.countries.map(c => (
                      <li key={c.code} className="text-xs text-gray-400 flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${a.dot}`}></span>
                        {c.name}
                        {c.hub && <span className="ml-1 text-[8px] bg-orange-600 text-white px-1 rounded">HUB</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default WorldMap;
